import { default as diff } from "changeset";
import { default as dotProp } from "dot-prop";
import { get } from "lodash";
import Utility from "./../Utility";
import { isMetadataKeyPath } from "./functions";
import { MapTypes } from "./MapTypes";

/**
 * JSDoc imports.
 *
 * @import { BaseChangeItemChangeset } from "@app/types";
 */

export class ChangeSets {
	getDiff(previousObject, changedObject) {
		const _diff = diff(previousObject, changedObject);
		return _diff.map((d) => {
			const previousValue = get(previousObject, d.key);
			return {
				...d,
				previousValue
			};
		});
	}

	/** @returns {BaseChangeItemChangeset[]} */
	getFilteredChangeSet(previousObject, changedObject) {

		const filteredChangeSet = this.getDiff(previousObject, changedObject).filter((diff) => {
			return (
				// versions
				!(diff.key.join(".").indexOf("versions") === 0 && diff.key.join(".").endsWith("timeAddedToFlow")) &&
				!(diff.key.join(".").indexOf("versions") === 0 && diff.key.join(".").endsWith("flowVersion")) &&
				!(diff.key.join(".").indexOf("versions") === 0 && diff.key.join(".").endsWith("version")) &&
				!(diff.key.join(".").indexOf("versions") === 0 && diff.key.join(".").endsWith("lastLocalChange")) &&
				!(diff.key.join(".").indexOf("flow") !== -1) &&
				// localVariables
				!(diff.key.join(".").indexOf("localVariables") === 0 && diff.key.join(".").endsWith("lastLocalChange")) &&
				!(diff.key.join(".").indexOf("localVariables") === 0 && diff.key.join(".").endsWith("disabledInFlow")) &&
				// validaion errors
				diff.key.join(".").indexOf("props.error.inputValRules") === -1
			);
		});

		if (filteredChangeSet.every((diff) => isMetadataKeyPath(diff.key))) {
			// Return an empty changeset if only metadata properties were updated.
			return [];
		}

		return filteredChangeSet;
	}

	/**
	 * Allows for changeset to capture previous state of object
	 * @param {string} mode 'normal' and 'previous'
	 */
	setMode(mode = "normal") {
		this.mode = mode;
	}
	/**
	 *
	 * @param {object} previousObject node being compared to
	 * @param {object} changedObject node that generates a diff when compared to previousNode
	 * @returns {BaseChangeItemChangeset[]} changeset diff expressed as a list of transformation operations.
	 * See changeset https://github.com/eugeneware/changeset
	 */
	getChangeSet(previousObject, changedObject, mapType, mode = "normal") {
		// normal captures new values
		// previous captures previous values *** needed for Git-like diffing
		this.setMode(mode);

		let changeset = this.getFilteredChangeSet(previousObject, changedObject);

		if (changeset.filter((diff) => diff.key.join(".").startsWith("localVariables")).length > 0) {
			changeset = changeset.filter((diff) => !diff.key.join(".").startsWith("localVariables"));
			changeset.push({ key: "localVariables".split(".") });
		}

		if (changeset.filter((diff) => diff.key.join(".").startsWith("props.config.inputs")).length > 0) {
			changeset = changeset.filter((diff) => !diff.key.join(".").startsWith("props.config.inputs"));
			changeset.push({ key: "props.config.inputs".split(".") });
		}

		if (changeset.filter((diff) => diff.key.join(".").startsWith("props.config.outputs")).length > 0) {
			changeset = changeset.filter((diff) => !diff.key.join(".").startsWith("props.config.outputs"));
			changeset.push({ key: "props.config.outputs".split(".") });
		}

		if (changeset.filter((diff) => diff.key.join(".").startsWith("props.config.function.arguments")).length > 0) {
			changeset = changeset.filter((diff) => !diff.key.join(".").startsWith("props.config.function.arguments"));
			changeset.push({ key: "props.config.function.arguments".split(".") });
		}

		if (changeset.filter((diff) => diff.key.join(".").startsWith("props.config.function.outputs")).length > 0) {
			changeset = changeset.filter((diff) => !diff.key.join(".").startsWith("props.config.function.outputs"));
			changeset.push({ key: "props.config.function.outputs".split(".") });
		}

		if (changeset.filter((diff) => diff.key.join(".").startsWith("moduleProperties.props.config.inputs")).length > 0) {
			changeset = changeset.filter((diff) => !diff.key.join(".").startsWith("moduleProperties.props.config.inputs"));
			changeset.push({ key: "moduleProperties.props.config.inputs".split(".") });
		}

		// TODO: this needs to be tested on canvas links
		if (changeset.filter((diff) => diff.key.join(".").startsWith("props.config.condition")).length > 0) {
			changeset = changeset.filter((diff) => !diff.key.join(".").startsWith("props.config.condition"));
			changeset.push({ key: "props.config.condition".split(".") });
		}

		switch (mapType) {
			case MapTypes.BRANCHING:
				const previousBranchingArray = previousObject[MapTypes.BRANCHING] || [];
				changeset = this.getDiff(previousBranchingArray, changedObject[MapTypes.BRANCHING]);

				break;
			case MapTypes.BRANCHING_MAP:
				const previousBranchingMap = previousObject[MapTypes.BRANCHING_MAP] || {};
				changeset = this.getDiff(previousBranchingMap, changedObject[MapTypes.BRANCHING_MAP]);

				break;
			case MapTypes.APPLICATION_MODULES:
				changeset.forEach((diff) => {
					// localVariables
					if (diff.key.join(".").startsWith("localVariables")) {
						diff = this.getArrayConfigDiff("localVariables", diff, previousObject, changedObject);
					}

					// inputs
					if (diff.key.join(".").startsWith("props.config.inputs")) {
						diff = this.getArrayConfigDiff("props.config.inputs", diff, previousObject, changedObject);
					}
					// outputs
					if (diff.key.join(".").startsWith("props.config.outputs")) {
						diff = this.getArrayConfigDiff("props.config.outputs", diff, previousObject, changedObject);
					}
				});

				break;

			case MapTypes.APPLICATION_OBJECTS:
				changeset.forEach((diff) => {
					// inputs
					if (diff.key.join(".").startsWith("props.config.inputs")) {
						diff = this.getArrayConfigDiff("props.config.inputs", diff, previousObject, changedObject);
					}
					// outputs
					if (diff.key.join(".").startsWith("props.config.outputs")) {
						diff = this.getArrayConfigDiff("props.config.outputs", diff, previousObject, changedObject);
					}
					// props.config.function.arguments
					if (diff.key.join(".").startsWith("props.config.function.arguments")) {
						diff = this.getArrayConfigDiff("props.config.function.arguments", diff, previousObject, changedObject);
					}
					// props.config.function.outputs
					if (diff.key.join(".").startsWith("props.config.function.outputs")) {
						diff = this.getArrayConfigDiff("props.config.function.outputs", diff, previousObject, changedObject);
					}

					// Inputs
					// // localIntents
					// if(diff.key.join('.').startWith('props.config.localIntents.')){
					//     previousObject.props.config.localIntents = previousObject.props.config.localIntents || [];
					//     diff = this.getPropsConfigInputDiff('props.config.localIntents.', diff, previousObject.props.config.localIntents);
					// }
					// Tabular Router
					// matchOrder
					// outputTo
				});

				break;

			case MapTypes.APPLICATION_INTENTS:
				// pass-through
				break;
			case MapTypes.APPLICATION_VARIABLES:
				// pass-through
				break;

			case MapTypes.MODULES:
				changeset.forEach((diff) => {
					// localVariables
					if (diff.key.join(".").startsWith("localVariables")) {
						diff = this.getArrayConfigDiff("localVariables", diff, previousObject, changedObject);
					}
					// inputs
					if (diff.key.join(".").startsWith("props.config.inputs")) {
						diff = this.getArrayConfigDiff("props.config.inputs", diff, previousObject, changedObject);
					}
					// outputs
					if (diff.key.join(".").startsWith("props.config.outputs")) {
						diff = this.getArrayConfigDiff("props.config.outputs", diff, previousObject, changedObject);
					}
					// // // props.config.function.arguments
					if (diff.key.join(".").startsWith("props.config.function.arguments")) {
						diff = this.getArrayConfigDiff("props.config.function.arguments", diff, previousObject, changedObject);
					}
					// // // props.config.function.outputs
					if (diff.key.join(".").startsWith("props.config.function.outputs")) {
						diff = this.getArrayConfigDiff("props.config.function.outputs", diff, previousObject, changedObject);
					}
				});

				break;

			case MapTypes.APPPROPERTIES:
				// paas-through
				break;
			case MapTypes.CCXML:
				// pass-through
				break;
			case MapTypes.COMPOUNDINTENTS:
				// pass-through
				break;
			case MapTypes.CSVDATA:
				// pass-through
				break;
			case MapTypes.FILLERPROMPTS:
				break;
			case MapTypes.FUNCTIONS:
				// pass-through
				break;
			case MapTypes.GLOBAL_ERROR_HANDLING:
				// pass-through
				break;
			case MapTypes.GLOBAL_EXCEPTION_HANDLING:
				// pass-through
				break;
			case MapTypes.GLOBAL_LOOP_HANDLING:
				// pass-through
				break;
			//INTENT_HANDLING
			case MapTypes.INTENT_HANDLING:
				const previousIntentHandlingObject = previousObject[MapTypes.INTENT_HANDLING] || {};
				changeset = this.getDiff(previousIntentHandlingObject, changedObject[MapTypes.INTENT_HANDLING]);

				break;
			case MapTypes.GLOBAL_INTENTS:
				changeset.forEach((diff) => {
					// inputs
					// TODO: condition?
					if (diff.key.join(".").startsWith("moduleProperties.props.config.inputs")) {
						diff = this.getArrayConfigDiff("moduleProperties.props.config.inputs", diff, previousObject, changedObject);
					}
				});
				break;
			case MapTypes.GRAPHQLSCHEMAS:
				// pass-through
				break;
			case MapTypes.LANGUAGES:
				break;
			case MapTypes.UNITTESTS:
				break;
			default:
				break;
		}

		// remove diffs marked for discard
		changeset = changeset.filter((diff) => !diff.discard);

		// remove items with empty propsDiff
		changeset = this.deleteEmptyItems(changeset);
		
		return changeset;
	}

	/**
	 * Remove items with empty propsDiff
	 * @param {object} changeset 
	 */
	deleteEmptyItems(changeset){
		
		changeset.forEach(cs => {
			if(cs.items){
				Object.entries(cs.items).forEach(([key, item]) => {
					if(item.action === 'modify' && item.propsDiff && item.propsDiff.length === 0){
						delete cs.items[key];
					}
				});
			}
		});

		return changeset;
	}
	/**
	 * Generic method to generate a diff on arrays whose items have ids
	 * @param {string} path e.g.: 'props.config.inputs'
	 * @param {object} diff
	 * @param {object} previousObject
	 * @param {object} changedObject
	 */
	getArrayConfigDiff(path, diff, previousObject, changedObject) {
		const previousConfig = dotProp.get(previousObject, path, []);
		const changedConfig = dotProp.get(changedObject, path, []);

		// added and modified
		changedConfig.forEach((changedItem) => {
			// this idKey is for setting up a changeset item (it can be a uuid, an argument name, a variable name...
			// this depends on which key-property is found first: "id", "argumentName", "varName", etc.)
			let idKey = Utility.getChangeSetIdKey(changedItem); // default key value

			// try to find if there was a previous item with the same id key.
			const previousItem = previousConfig.find((previousItem) => {
				const [isEqual, key] = Utility.getItemsEquality(changedItem, previousItem);

				if (isEqual) {
					idKey = key;
				}

				return isEqual;
			});
			const idStr = changedItem[idKey];

			if (idKey === "" || !idStr) {
				return; // there is no point on proceeding if there is no key or item
			}

			delete diff.value;
			diff.items = diff.items || {};
			diff.items[idStr] = diff.items[idStr] || {};
			diff.items[idStr].item = changedItem;

			// if not found, then this is a new item (`add`)
			if (!previousItem) {
				diff.items[idStr].action = "add";

				// added and deleted not relevant to previous changesets
				if (this.mode === "previous") {
					diff.discard = true;
				}
			} else { // else, apply the update to the previousItem (`modify`)
				diff.items[idStr].action = "modify";
				diff.items[idStr].propsDiff = this.getDiff(previousItem, changedItem);
			}
		});

		// deleted
		previousConfig.forEach((previousItem) => {
			let idKey = Utility.getChangeSetIdKey(previousItem);
			const changedItem = changedConfig.find((changedItem) => {
				const [isEqual, key] = Utility.getItemsEquality(changedItem, previousItem);

				if (isEqual) {
					idKey = key;
				}

				return isEqual;
			});
			const idStr = changedItem ? changedItem[idKey] : previousItem[idKey];

			if (idKey === "" || !idStr) {
				return; // there is no point on proceeding if there is no key or item
			}

			if (!changedItem) {
				delete diff.value;
				diff.items = diff.items || {};
				diff.items[idStr] = diff.items[idStr] || {};
				diff.items[idStr].action = "delete";
				diff.items[idStr].item = previousItem;

				// added and deleted not relevant to previous changesets
				if (this.mode === "previous") {
					diff.discard = true;
				}
			}
		});

		return diff;
	}

	/**
	 * 
	 * @param {*} path 
	 * @param {*} diff 
	 * @param {*} previousObject 
	 * @param {*} changedObject 
	 */
	getFlatArrayConfigDiff(path, diff, previousObject, changedObject){
		const previousConfig = dotProp.get(previousObject, path, []);
		const changedConfig = dotProp.get(changedObject, path, []);

		// item added
		changedConfig.forEach((changedItem) => {
			if (previousConfig.findIndex((previousItem) => previousItem === changedItem) === -1) {
				diff["add"] = diff["add"] || [];
				diff["add"].push(changedItem);
			}

		});

		// item deleted
		previousConfig.forEach((previousItem) => {
			if (changedConfig.findIndex((changedItem) => changedItem === previousItem) === -1) {
				diff["delete"] = diff["delete"] || [];
				diff["delete"].push(previousItem);
			}

		});
		
		return diff
	}
}

export default ChangeSets;
